import { useCallback, useEffect, useRef } from 'react';
import {
  type NotificationAddData,
  generateNotificationId,
} from '@doxyme/ui/components/foundations';
import { useAppDispatch } from '../useReduxToolkit';
import {
  addNotification,
  dismissNotification,
} from '../../features/notifications/notifications.slice';

type UseReplaceableNotificationReturn = {
  show: (
    type: NotificationAddData['type'],
    options?: Omit<NotificationAddData, 'id' | 'type'>,
  ) => void;
  hide: () => void;
};

/**
 * A hook to manage a single replaceable notification within its instance.
 *
 * This hook was designed to handle cases where we want to ensure that only one notification is visible
 * at a time, while avoiding interference with other notifications in the system. When a new notification
 * is triggered, it automatically dismisses any currently displayed notification within the same hook instance,
 * ensuring a clean, user-friendly experience with no notification overlap.
 *
 * The notification is cleared when the component is unmounted, preventing stale notifications from persisting.
 * This implementation guarantees that each hook instance manages its own notification state independently,
 * without affecting notifications managed by other parts of the application.
 *
 * Usage:
 * - `show(type, options?)`: Displays a notification of the specified `type`.
 *   - `type`: The notification type (e.g., 'error', 'success').
 *   - `options?`: Optional object containing additional notification data.
 * - `hide()`: Hides the current active notification in the scope of this hook instance.
 */
export const useReplaceableNotification =
  (): UseReplaceableNotificationReturn => {
    const dispatch = useAppDispatch();
    const notification = useRef<string | undefined>(undefined);

    const hide = useCallback((): void => {
      if (notification.current !== undefined) {
        void dispatch(dismissNotification({ id: notification.current }));
      }
    }, []);

    const show: UseReplaceableNotificationReturn['show'] = useCallback(
      (type, options): void => {
        hide();

        const id = generateNotificationId();
        notification.current = id;
        void dispatch(
          addNotification({
            id: notification.current,
            type,
            ...options,
            callback: (state) => {
              if (id === notification.current) {
                notification.current = undefined;
              }

              void options?.callback?.(state);
            },
          }),
        );
      },
      [],
    );

    useEffect(() => hide, []);

    return { show, hide };
  };
